/**
 * Iframe Manager
 * Handles creation, positioning, and lifecycle of the widget iframe
 */

import type { WidgetConfig } from "../shared/config";
import { getInitialIframeDimensions, getLauncherBorderRadius, resolveLauncherConfig, CHAT_WINDOW_BORDER_RADIUS } from "../shared/launcher";
import { debug } from "./utils/debug";

const MOBILE_MEDIA_QUERY =
  "(max-width: 480px), (max-height: 600px) and (orientation: landscape)";

export class IframeManager {
  private iframe: HTMLIFrameElement | null = null;
  private container: HTMLElement | null = null;
  private mediaQuery: MediaQueryList;
  private mediaQueryListener: ((e: MediaQueryListEvent) => void) | null = null;
  private viewportChangeCallback: ((isMobile: boolean) => void) | null = null;
  private isOpen = false;
  private closedDimensions: { width: number; height: number };
  private lastWidth: number;
  private lastHeight: number;

  constructor(private config: WidgetConfig) {
    this.mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    this.closedDimensions = getInitialIframeDimensions(config);
    this.lastWidth = this.closedDimensions.width;
    this.lastHeight = this.closedDimensions.height;
  }

  isMobile(): boolean {
    return this.mediaQuery.matches;
  }

  /**
   * Register a callback fired whenever the viewport crosses the mobile breakpoint.
   * Loader uses this to notify the widget via postMessage.
   */
  onViewportChange(callback: (isMobile: boolean) => void): void {
    this.viewportChangeCallback = callback;
  }

  createIframe(): HTMLIFrameElement {
    // Clean up existing iframe
    this.destroy();

    // Create container
    this.container = document.createElement("div");
    this.container.id = "apten-widget-container";
    this.container.style.cssText = this.getContainerStyles();

    // Create iframe
    this.iframe = document.createElement("iframe");
    this.iframe.src = this.getIframeUrl();
    this.iframe.style.cssText = this.getIframeStyles();
    this.iframe.setAttribute(
      "sandbox",
      "allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
    );
    this.iframe.setAttribute("title", "Apten Chat Widget");

    // On mobile, skip the width/height transition — jumping between the bubble
    // size and fullscreen mid-animation causes visible reflow jank.
    if (this.isMobile()) {
      this.iframe.style.transition = "opacity 0.3s ease";
    } else {
      this.iframe.style.transition = "all 0.3s ease";
    }

    this.container.appendChild(this.iframe);
    document.body.appendChild(this.container);

    // Listen for viewport crossings so the widget can reflow when rotating,
    // resizing the browser, or switching between desktop and mobile layouts.
    this.mediaQueryListener = () => this.handleViewportChange();
    if (typeof this.mediaQuery.addEventListener === "function") {
      this.mediaQuery.addEventListener("change", this.mediaQueryListener);
    } else {
      // Safari < 14 fallback
      (this.mediaQuery as any).addListener(this.mediaQueryListener);
    }

    return this.iframe;
  }

  private handleViewportChange(): void {
    // Re-apply sizing for the current open/closed state under the new viewport class
    const mobile = this.isMobile();
    debug.log("[Iframe Manager] Viewport changed:", { mobile, isOpen: this.isOpen });

    // Adjust transition based on new viewport
    if (this.iframe) {
      this.iframe.style.transition = mobile
        ? "opacity 0.3s ease"
        : "all 0.3s ease";
    }

    // Re-apply current sizing (synthesizes a resize using the state we already track)
    this.applyCurrentSizing();

    // Notify the loader → widget
    if (this.viewportChangeCallback) {
      this.viewportChangeCallback(mobile);
    }
  }

  private applyCurrentSizing(): void {
    // Replay the widget's last reported dimensions. On mobile-open this is
    // irrelevant (resize() clamps to 100%/100%), but on desktop-open we need
    // the real pixel dimensions the widget last asked for. Passing isOpen
    // explicitly avoids relying on a width-based heuristic.
    this.resize(this.lastWidth, this.lastHeight, this.isOpen);
  }

  private getLoaderOrigin(): string {
    try {
      // Method 1: Find the script tag that loaded the loader (most reliable)
      const scripts = document.querySelectorAll('script[src*="apten-loader"]');
      if (scripts.length > 0) {
        const scriptSrc = (scripts[0] as HTMLScriptElement).src;
        const scriptUrl = new URL(scriptSrc);
        debug.log(
          "[Iframe Manager] Detected loader origin from script tag:",
          scriptUrl.origin
        );
        return scriptUrl.origin;
      }
    } catch (error) {
      debug.warn(
        "[Iframe Manager] Failed to get origin from script tag:",
        error
      );
    }

    try {
      // Method 2: Use import.meta.url if available (development only)
      if (import.meta.env.DEV && import.meta.url) {
        const scriptUrl = new URL(import.meta.url);
        debug.log(
          "[Iframe Manager] Detected loader origin from import.meta.url:",
          scriptUrl.origin
        );
        return scriptUrl.origin;
      }
    } catch (error) {
      debug.warn(
        "[Iframe Manager] Failed to get origin from import.meta.url:",
        error
      );
    }

    // Fallback to current page origin
    debug.warn(
      "[Iframe Manager] Using fallback origin:",
      window.location.origin
    );
    return window.location.origin;
  }

  private getIframeUrl(): string {
    // Development vs production URL
    if (import.meta.env.DEV) {
      const loaderOrigin = this.getLoaderOrigin();
      const iframeUrl = `${loaderOrigin}/src/widget.html`;
      debug.log("[Iframe Manager] Using development iframe URL:", iframeUrl);
      return iframeUrl;
    }

    // Production: Use the same origin as the loader script
    const loaderOrigin = this.getLoaderOrigin();
    const iframeUrl = `${loaderOrigin}/widget.html`;
    debug.log("[Iframe Manager] Using production iframe URL:", iframeUrl);
    return iframeUrl;
  }

  private getContainerStyles(): string {
    const { position } = this.config;
    const baseStyles = `
      position: fixed;
      z-index: 999999;
      pointer-events: auto;
    `;

    // Mobile + open = fullscreen sheet, ignore configured corner position.
    if (this.isMobile() && this.isOpen) {
      return baseStyles + "inset: 0;";
    }

    switch (position) {
      case "bottom-left":
        return baseStyles + "bottom: 20px; left: 20px;";
      case "top-right":
        return baseStyles + "top: 20px; right: 20px;";
      case "top-left":
        return baseStyles + "top: 20px; left: 20px;";
      default: // bottom-right
        return baseStyles + "bottom: 20px; right: 20px;";
    }
  }

  private getInitialBorderRadius(): string {
    const launcher = resolveLauncherConfig(
      this.config.launcher,
      this.config.launcher?.content === "logo",
    );

    return getLauncherBorderRadius(launcher);
  }

  private getIframeBoxShadow(): string {
    return this.config.theme === "dark"
      ? "0 20px 45px rgba(0, 0, 0, 0.38)"
      : "0 20px 45px rgba(39, 39, 42, 0.16)";
  }

  private getIframeStyles(): string {
    return `
      width: ${this.closedDimensions.width}px;
      height: ${this.closedDimensions.height}px;
      border: none;
      border-radius: ${this.getInitialBorderRadius()};
      box-shadow: ${this.getIframeBoxShadow()};
      background: transparent;
      overflow: hidden;
      transition: all 0.3s ease;
      visibility: hidden;
      opacity: 0;
    `;
  }

  resize(width: number, height: number, isOpen?: boolean, borderRadius?: string): void {
    if (!this.iframe || !this.container) return;

    // Prefer the widget's explicit open/closed signal when provided. Falling
    // back to a dimension heuristic is fragile — if the canonical sizes ever
    // straddle the threshold differently, open/closed detection breaks
    // silently. Both sides are under our control, so we always pass isOpen
    // explicitly now.
    if (isOpen !== undefined) {
      this.isOpen = isOpen;
    } else {
      this.isOpen = width > 200;
    }

    // Remember the latest dimensions so applyCurrentSizing() can replay them
    // when the viewport class flips without hardcoding canonical values.
    this.lastWidth = width;
    this.lastHeight = height;

    // resize() must be idempotent across viewport-class changes: when the
    // viewport crosses the mobile breakpoint while the widget is open, we
    // re-enter this function and need to fully overwrite any inline styles
    // the previous branch left behind. So every branch writes the full set
    // of inline properties the other branches touch (width, height,
    // borderRadius, boxShadow) and always re-applies container styles.

    const mobileOpen = this.isMobile() && this.isOpen;

    if (mobileOpen) {
      // Fullscreen sheet — iframe fills the container, container is inset:0.
      this.iframe.style.width = "100%";
      this.iframe.style.height = "100%";
      this.iframe.style.borderRadius = "0";
      this.iframe.style.boxShadow = "none";
      this.iframe.style.background =
        this.config.theme === "dark" ? "#18181b" : "#ffffff";
    } else {
      // Every non-fullscreen state (desktop open, desktop closed, mobile
      // closed bubble) renders as a corner-anchored box with the default
      // radius/shadow. The only thing that differs is the dimensions, which
      // come from the widget's RESIZE message.
      this.iframe.style.width = `${width}px`;
      this.iframe.style.height = `${height}px`;
      this.iframe.style.borderRadius = this.isOpen
        ? CHAT_WINDOW_BORDER_RADIUS
        : borderRadius ?? this.getInitialBorderRadius();
      this.iframe.style.background = this.isOpen
        ? this.config.theme === "dark"
          ? "#18181b"
          : "#ffffff"
        : "transparent";
      this.iframe.style.boxShadow = this.getIframeBoxShadow();
    }

    this.container.style.cssText = this.getContainerStyles();
  }

  showWidget(): void {
    if (this.iframe) {
      this.iframe.style.visibility = "visible";
      this.iframe.style.opacity = "1";
    }
  }

  hideWidget(): void {
    if (this.iframe) {
      this.iframe.style.visibility = "hidden";
      this.iframe.style.opacity = "0";
    }
  }

  updatePosition(position: string): void {
    if (this.container) {
      // Update config and reapply styles
      this.config.position = position as any;
      this.container.style.cssText = this.getContainerStyles();
    }
  }

  updateTheme(theme: "light" | "dark"): void {
    this.config.theme = theme;
    if (this.iframe) {
      this.applyCurrentSizing();
    }
    // Theme will be passed to widget via postMessage
  }

  destroy(): void {
    if (this.mediaQueryListener) {
      if (typeof this.mediaQuery.removeEventListener === "function") {
        this.mediaQuery.removeEventListener("change", this.mediaQueryListener);
      } else {
        (this.mediaQuery as any).removeListener(this.mediaQueryListener);
      }
      this.mediaQueryListener = null;
    }
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this.iframe = null;
    this.container = null;
    this.isOpen = false;
    this.lastWidth = this.closedDimensions.width;
    this.lastHeight = this.closedDimensions.height;
  }
}
